import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dayjs } from 'dayjs';
import styled from 'styled-components';
import { Button, DailyCalendar, H3, Link, Text, WeeklyCalendar } from 'components';
import { IWeekView } from 'components/Calendar/WeeklyCalendar';
import { useFeatureFlags } from 'contexts/featureFlags';
import { theme, toRem } from 'design';
import { useWindowSize } from 'hooks';
import { routeConstants } from 'routes';
import { browserHistory } from 'routes/Router';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { CalculatorRules } from 'store/CalculatorRules';
import { getSitePaymentsRequest, Payments, selectPaymentSetsForSite } from 'store/Collections';
import { DaySegments, getDaySegmentsRequest, selectDaySegments } from 'store/Collections/DaySegments';
import { selectSiteSettings } from 'store/Site/selectors';
import { IStore } from 'store/types';
import { ApiQueryOperator, Days, FeatureFlags, ICalculatorPaymentSegment, IDaySegment, IEmployerSite } from 'types';
import { formatDateForApi, formatTime } from 'utils/date';
import { isDatePayable } from 'utils/tipCalculator';

interface IDateSelectorProps {
  currentSite: IEmployerSite;
  currentSiteId: string;
  onNext: (day: Dayjs, segments: ICalculatorPaymentSegment[]) => void;
  pageHeaderContent: React.ReactNode;
}

const WeeklyToDailyBreakpoint = 870;

const CalendarWrapper = styled.section`
  display: flex;
  flex-flow: column nowrap;
  padding: 0 ${toRem(20)};

  @media (max-width: ${400}px) {
    padding: 0 ${toRem(12)};
    width: 100%;
  }
`;

const ExpandedContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;

const ContentWrapper = styled.div<{ hasHeader?: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  background-color: ${theme.color.neutral[50]};
  border-radius: ${({ hasHeader }) => (hasHeader ? `0 0 ${toRem(8)} ${toRem(8)}` : toRem(8))};
  padding: ${toRem(24)};
  width: 100%;
`;

const SelectedDateContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  a {
    font-weight: 500;
    text-decoration: none;
    font-size: ${toRem(12)};
  }

  @media (max-width: ${WeeklyToDailyBreakpoint}px) {
    flex-flow: column nowrap;

    h3 {
      margin-bottom: ${toRem(10)};
    }
  }
`;

const DaySegmentHeader = styled.div`
  display: flex;
  justify-content: space-between;

  p {
    color: ${theme.color.neutral[500]};
  }
`;

const DaySegment = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: ${toRem(8)};
  width: 100%;

  &:not(:last-child) {
    padding-bottom: ${toRem(8)};
    border-bottom: 1px solid ${theme.color.neutral[200]};
  }

  & > *:first-child {
    word-break: break-word;
  }

  @media (max-width: ${WeeklyToDailyBreakpoint}px) {
    flex-flow: column nowrap;

    & > *:not(:last-child) {
      padding-bottom: ${toRem(4)};
    }
  }
`;

const BusinessDayMessage = styled(Text)<{ background?: string }>`
  background-color: ${({ background }) => background || theme.color.neutral[100]};
  border-radius: ${toRem(8)} ${toRem(8)} 0 0;
  text-align: center;
  padding: ${toRem(8)};
  width: 100%;
`;

const Footer = styled.div`
  margin-top: ${toRem(20)};
  display: flex;
  flex-direction: column;

  button {
    width: ${toRem(210)};

    &:first-child {
      margin-bottom: ${toRem(12)};
    }
  }

  /* Desktop styles */
  @media (min-width: ${theme.breakpoint.small}px) {
    flex-direction: row;

    button {
      &:first-child {
        margin-right: ${toRem(12)};
        margin-bottom: 0;
      }
    }
  }
`;

const StyledText = styled(Text)`
  @media (max-width: ${WeeklyToDailyBreakpoint}px) {
    display: none;
  }
`;

const DateSelector = ({ currentSite, currentSiteId, onNext, pageHeaderContent }: IDateSelectorProps) => {
  const dispatch = useDispatch();
  const featureFlags = useFeatureFlags([FeatureFlags.TIP_CALCULATOR_DAY_PAID]);
  const { width: windowWidth } = useWindowSize();

  const { endDayMidnightOffset, timezone } = useSelector(selectSiteSettings()) || {};
  const daySegmentsForSite = useSelector(selectDaySegments());
  const paymentSetsForCalendarDates = useSelector(selectPaymentSetsForSite());

  const isLoadingDaySegments = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, DaySegments.GET_DAY_SEGMENTS_REQUEST));
  const isLoadingCalculatorRules = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, CalculatorRules.GET_CALCULATOR_RULES_REQUEST));

  const isLoadingPaymentsForSite = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payments.GET_SITE_PAYMENTS_REQUEST));

  const [visibleDays, setVisibleDays] = useState<IWeekView[]>([]);
  const [selectedDay, setSelectedDay] = useState<Dayjs>();
  const [selectedDayPaid, setSelectedDayPaid] = useState<boolean>(false);
  const isSelectedDayOver = selectedDay && isDatePayable(selectedDay, endDayMidnightOffset, timezone, daySegmentsForSite?.data?.length > 0 ? daySegmentsForSite?.data : undefined);
  const dayPaidEnabled = featureFlags[FeatureFlags.TIP_CALCULATOR_DAY_PAID]?.enabled;

  useEffect(() => {
    if (currentSite?.id && currentSite.settings?.tipCalculator) {
      dispatch(getDaySegmentsRequest({ siteId: currentSite.id, options: { sort: 'created_at', limit: 0 } }));
    }
  }, [currentSite]);

  const fetchPaymentsForVisibleDays = useCallback(
    (days: IWeekView[]) => {
      // fetch site payouts, filter by the visible days in the calendar and `calculator` payment type
      if (dayPaidEnabled && !!days?.length) {
        const startDate = days[0].weekday;
        const endDate = days[days.length - 1].weekday;

        dispatch(
          getSitePaymentsRequest(currentSiteId, {
            q: [
              {
                key: 'ref_type',
                operator: ApiQueryOperator.eq,
                value: 'calculator',
              },
              { key: 'payment_date', operator: ApiQueryOperator.gte, value: formatDateForApi(startDate) },
              { key: 'payment_date', operator: ApiQueryOperator.lte, value: formatDateForApi(endDate) },
            ],
          })
        );
      }
    },
    [dayPaidEnabled]
  );

  const mappedVisibleDaysToPaidDays = useMemo(
    () =>
      visibleDays?.map((day) => ({
        ...day,
        includeCheck: paymentSetsForCalendarDates?.some((paidDay) => paidDay.paymentDate === formatDateForApi(day.weekday)) || false,
      })),
    [visibleDays, paymentSetsForCalendarDates]
  );

  const mappedDaySegments = useMemo(
    () =>
      (daySegmentsForSite?.data || [])
        .reduce((acc: ICalculatorPaymentSegment[], daySegment: IDaySegment) => {
          const selectedDayName = selectedDay?.format('dddd').toLocaleUpperCase() as Days;

          if (selectedDayName && daySegment?.days.includes(selectedDayName)) {
            acc.push({
              ...daySegment,
              hours: `${formatTime(daySegment.start)} - ${formatTime(daySegment.end)}`,
              cash: 0,
              credit: 0,
              creditCardFee: 0,
            });
          }

          return acc;
        }, [])
        // sort segments by start time
        .sort((a: ICalculatorPaymentSegment, b: ICalculatorPaymentSegment) => {
          const start = parseInt(a.start.split(':').join(''), 10);
          const end = parseInt(b.start.split(':').join(''), 10);
          return start - end;
        })
        // strip leading 0's from hours
        .map((segment) => {
          // eslint-disable-next-line no-param-reassign
          segment.hours = segment.hours
            .split(' - ')
            .map((time) => (time[0] === '0' ? time.slice(1) : time))
            .join(' - ');

          return segment;
        }),
    [daySegmentsForSite, selectedDay, currentSite]
  );

  const dayMessage: { statusColor: string; message: ReactNode; dataCy: string } | null = useMemo(() => {
    if (!isSelectedDayOver) {
      return {
        message: 'Tips cannot be issued until the business day has concluded.',
        statusColor: theme.color.danger.light,
        dataCy: 'select-date-error',
      };
    }

    if (dayPaidEnabled && selectedDayPaid) {
      return {
        message: 'You have already issued tips for this day.',
        statusColor: theme.color.warning.light,
        dataCy: 'selected-date-paid-status',
      };
    }

    if (mappedDaySegments?.length < 1) {
      return {
        message: (
          <>
            Day segments have not been configured for this day.{' '}
            <Link to={routeConstants.private.settingsDaySegments}>
              <b>Click here</b>
            </Link>{' '}
            to configure day segments.
          </>
        ),
        statusColor: theme.color.danger.light,
        dataCy: 'select-date-segment-error',
      };
    }

    return null;
  }, [isSelectedDayOver, dayPaidEnabled, selectedDayPaid, mappedDaySegments]);

  const getDayInfo = useMemo(() => {
    const paymentSetforDate = paymentSetsForCalendarDates?.find((item) => item.paymentDate === selectedDay?.format('YYYY-MM-DD'));

    return (
      <ExpandedContainer>
        {dayMessage && (
          <BusinessDayMessage data-cy={dayMessage.dataCy} background={dayMessage.statusColor} size="small" fontWeight="medium">
            {dayMessage.message}
          </BusinessDayMessage>
        )}
        <ContentWrapper hasHeader={!!dayMessage}>
          {dayPaidEnabled && (
            <SelectedDateContainer style={mappedDaySegments?.length ? { marginBottom: toRem(20) } : {}} data-cy="selected-date-header">
              <H3>{selectedDay?.format('MMMM Do, YYYY')}</H3>
              {selectedDayPaid && paymentSetforDate && <Link to={`/payments/payment-set/${paymentSetforDate.id}`}>View the payment set</Link>}
            </SelectedDateContainer>
          )}
          {!!mappedDaySegments.length && (
            <>
              <DaySegmentHeader data-cy="day-segment-header">
                <Text size="xsmall" fontWeight="medium">
                  Day Segment
                </Text>
                <StyledText size="xsmall" fontWeight="medium" className="segment-hours">
                  Hours
                </StyledText>
              </DaySegmentHeader>
              {mappedDaySegments.map((segment: ICalculatorPaymentSegment) => (
                <DaySegment data-cy="day-segment-info" key={`day-segment-info-${segment.id}`}>
                  <Text size="xsmall" fontWeight="medium">
                    {segment.name}
                  </Text>
                  <Text size="xsmall">{segment.hours}</Text>
                </DaySegment>
              ))}
            </>
          )}
        </ContentWrapper>
      </ExpandedContainer>
    );
  }, [mappedDaySegments]);

  return (
    <>
      {pageHeaderContent}

      {windowWidth && (
        <CalendarWrapper>
          <H3 data-cy="select-date-title" marginBottom={20}>
            Select a day to payout tips
          </H3>
          {windowWidth > WeeklyToDailyBreakpoint ? (
            <WeeklyCalendar
              expandedDay={getDayInfo}
              previousWeekMax={4}
              futureWeekMax={0}
              isLoading={isLoadingDaySegments || isLoadingCalculatorRules || isLoadingPaymentsForSite}
              onDayClick={(day: IWeekView) => {
                setSelectedDay(day.weekday);
                if (dayPaidEnabled) setSelectedDayPaid(day.includeCheck);
              }}
              onCalNavigation={(days: IWeekView[]) => {
                setVisibleDays(days);
                fetchPaymentsForVisibleDays(days);
              }}
              customizedVisibleWeek={dayPaidEnabled && paymentSetsForCalendarDates?.length ? mappedVisibleDaysToPaidDays : undefined}
              selectedDay={selectedDay}
            />
          ) : (
            <DailyCalendar
              expandedDay={getDayInfo}
              previousDayMax={30}
              futureDayMax={0}
              includeCheck={dayPaidEnabled && !!mappedVisibleDaysToPaidDays?.length && mappedVisibleDaysToPaidDays[0].includeCheck}
              isLoading={isLoadingDaySegments || isLoadingCalculatorRules || isLoadingPaymentsForSite}
              onDayClick={(day: Dayjs) => {
                setSelectedDay(day);
                if (dayPaidEnabled) setSelectedDayPaid(!!mappedVisibleDaysToPaidDays?.length && mappedVisibleDaysToPaidDays[0].includeCheck);
              }}
              onCalNavigation={(day: Dayjs) => {
                setVisibleDays([{ weekday: day }]);
                fetchPaymentsForVisibleDays([{ weekday: day }]);
              }}
              selectedDay={selectedDay}
            />
          )}
        </CalendarWrapper>
      )}
      <Footer>
        <Button data-cy="back-btn" label="Back" appearance="white" onClick={() => browserHistory.push(routeConstants.private.makePayment)} />
        <Button data-cy="next-btn" label="Next Step" appearance="primary" disabled={!selectedDay || !isSelectedDayOver || mappedDaySegments?.length < 1} onClick={() => onNext(selectedDay, mappedDaySegments)} />
      </Footer>
    </>
  );
};

export default DateSelector;
